/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getPayments, {
	getPaymentsParams,
	getPaymentsResponse,
} from 'api/payment/getPayment'
import { Payment } from 'types'

/**
 * Types for the slice of Payment in the store
 */
export interface PaymentState {
	records: Payment[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
}

/**
 * Initial state of the slice Payment
 */
const initialState: PaymentState = {
	records: [],
	totalCount: 0,
	totalPages: 0,
	currentPage: 1,
	loading: false,
}

/**
 * Action for getting Payment
 *
 * Tries to get Payment from the API and returns the result in the payload
 *
 * @param {getPaymentsParams} options - Options for the API call
 * @returns {Promise<getPaymentsResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getPaymentsAction = createAsyncThunk<
	getPaymentsResponse,
	getPaymentsParams
>('payment/getPaymentsAction', async (options, { rejectWithValue }) => {
	try {
		const response = await getPayments(options)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const paymentSlice = createSlice({
	name: 'payment',
	initialState,
	reducers: {
		clearPayment: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getPaymentsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getPaymentsAction.fulfilled, (state, action) => {
			const { records, totalCount, totalPages, currentPage } =
				action.payload.response
			state.records = records
			state.totalCount = totalCount
			state.totalPages = totalPages
			state.currentPage = currentPage
			state.loading = false
		})
		builder.addCase(getPaymentsAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { clearPayment } = paymentSlice.actions

export default paymentSlice.reducer
